import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../../utils/apiBase';

interface NotifyEvent {
  id: string;
  label: string;
}

interface StudioSettingsShape {
  deliveryTurnaroundDays: number;
  reviewDelayDays: number;
  notifyEmail: string;
  notifications: Record<string, boolean>;
  updatedAt?: string;
}

interface SettingsResponse {
  settings: StudioSettingsShape;
  events: NotifyEvent[];
  effectiveNotifyEmail: string;
  defaultNotifyEmail: string;
}

/**
 * Studio-wide workflow settings: who gets told when a client does something,
 * and how long Carmen normally takes to deliver edited photographs. The
 * turnaround sets each job's delivery date when the shoot is marked done; the
 * red editing entries in the diary and the "days left" reminders count to it.
 */
const StudioSettings = () => {
  const [data, setData] = useState<SettingsResponse | null>(null);
  const [draft, setDraft] = useState<StudioSettingsShape | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    fetch(`${API_BASE_URL}/studio/settings`)
      .then(async (response) => {
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.error || 'Settings could not be loaded.');
        if (!active) return;
        setData(body);
        setDraft(body.settings);
      })
      .catch((loadError) => {
        if (active) setError(loadError instanceof Error ? loadError.message : 'Settings could not be loaded.');
      });
    return () => {
      active = false;
    };
  }, []);

  const save = async () => {
    if (!draft || saving) return;
    setSaving(true);
    setSaved('');
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'Settings could not be saved.');
      setData(body);
      setDraft(body.settings);
      setSaved('Saved');
      setTimeout(() => setSaved(''), 2500);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Settings could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const events = data?.events || [];
  const allOn = draft ? events.every((event) => draft.notifications[event.id] !== false) : false;

  return (
    <div className="studio-home studio-settings" data-testid="studio-settings">
      <header className="studio-home-head">
        <div>
          <h2>Studio notifications</h2>
          <p>What reaches Carmen's inbox when a client acts, and how long editing normally takes.</p>
        </div>
      </header>
      {error ? <p className="studio-error" role="alert">{error}</p> : null}
      {!draft ? <p className="studio-note">Loading…</p> : (
        <div className="studio-split">
          <section className="studio-panel" aria-label="Email alerts">
            <h3 className="studio-section-label">Email alerts</h3>
            <label>
              Send alerts to
              <input
                type="email"
                value={draft.notifyEmail}
                placeholder={data?.defaultNotifyEmail || 'hello@carmenlorraine.com'}
                onChange={(event) => setDraft({ ...draft, notifyEmail: event.target.value })}
                aria-label="Alert email address"
                data-testid="notify-email"
              />
            </label>
            <p className="studio-note">
              Currently going to <strong>{data?.effectiveNotifyEmail}</strong>. Leave blank to use {data?.defaultNotifyEmail}.
              Every alert carries the client's message and a button that opens the right part of the job.
            </p>
            <div className="studio-toolbar" style={{ justifyContent: 'space-between' }}>
              <strong>Tell me when…</strong>
              <button
                type="button"
                className="studio-text-action"
                onClick={() => {
                  const next: Record<string, boolean> = {};
                  events.forEach((event) => { next[event.id] = !allOn; });
                  setDraft({ ...draft, notifications: next });
                }}
              >
                {allOn ? 'Turn all off' : 'Turn all on'}
              </button>
            </div>
            <ul className="studio-check-list">
              {events.map((event) => (
                <li key={event.id}>
                  <label className="studio-check">
                    <input
                      type="checkbox"
                      checked={draft.notifications[event.id] !== false}
                      onChange={(change) => setDraft({ ...draft, notifications: { ...draft.notifications, [event.id]: change.target.checked } })}
                      data-testid={`notify-${event.id}`}
                    />
                    <span>{event.label}</span>
                  </label>
                </li>
              ))}
            </ul>
          </section>

          <section className="studio-panel" aria-label="Editing and delivery">
            <h3 className="studio-section-label">Editing and delivery</h3>
            <label>
              Usual turnaround after a shoot (days)
              <input
                type="number"
                min={0}
                max={120}
                value={draft.deliveryTurnaroundDays}
                onChange={(event) => setDraft({ ...draft, deliveryTurnaroundDays: Number(event.target.value) })}
                aria-label="Delivery turnaround days"
                data-testid="turnaround-days"
              />
            </label>
            <p className="studio-note">
              When a shoot is marked done, the client's delivery date is set this many days later (it can be changed per job).
              The job then sits in the diary in red until the photographs are delivered, and Today counts down the days left.
            </p>
            <p className="studio-note">Once the photographs are delivered and the job is paid in full, the Google review request goes out on its own.</p>
            <div className="studio-toolbar">
              <button type="button" className="studio-primary" onClick={save} disabled={saving} data-testid="save-studio-settings">
                {saving ? 'Saving…' : 'Save settings'}
              </button>
              {saved ? <span className="studio-note">{saved}</span> : null}
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default StudioSettings;
